import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { APISERVICE } from '../services/api.service'
import { updateTeacherInfo } from '../redux/states/docente'
import { addTime, formatDate, subtractTime } from 'utils'
import { minutesToEndClass, typeChecks } from 'data/constants'
import { decryptAES } from 'utils/encryptionUtils/decryptAES'
import { encryptAES } from 'utils/encryptionUtils/encryptAES'
import { useServerDate } from './useServerDate'

const kEYLOCALSTORAGE = process.env.REACT_APP_ENCRYPTION_KEY

export function useSchedule() {
    const { teacherInfo, classStarted, infoSchedule } = useSelector((store) => store.teacher)
    const { serverDate, timeString, dateString, dateTimeString } = useServerDate()
    const [duracionNotificaciones, setDuracionNotificaciones] = useState(5)
    const [loading, setLoading] = useState(false)

    const dispatch = useDispatch()
    const getConfig = async () => {
        const url = 'configuracion/get?claves=duracion_notificaciones'
        const data = await APISERVICE.get(url, '')
        if (data.duracion_notificaciones) setDuracionNotificaciones(data.duracion_notificaciones.valor)
    }

    const getDailySchedules = async () => {
        try {
            setLoading(true)
            const url = 'docente/get-enabled-schedules?'
            const params = `id=${teacherInfo.teacherId}`
            const { success, schedules } = await APISERVICE.get(url, params)
            if (success) {
                validateSchedule(schedules)
            }
        } catch (error) {
            toast.error('Ocurrió un error')
        } finally {
            setLoading(false)
        }
    }

    const getInfo = async () => {
        await getConfig()
        await getDailySchedules()
    }

    const initialState = (endClass = '') => {
        let data = {
            infoSchedule: {},
            infoTimer: {},
            classStarted: {
                startClass: '',
                active: false,
                endClass: endClass,
            },
        }
        dispatch(updateTeacherInfo(data))
    }

    /**
     * Obtiene el horario de la clase siguiente del día y sus notificaciones.
     * @param {Array<object>} schedules Lista de horarios del día.
     */
    const validateSchedule = async (schedules) => {
        // Si hay un horario activo se toma el horario, en caso contrario se busca la siguiente clase
        const schedule = verifyActiveClass() ? infoSchedule.schedule : nextClass(schedules)
        if (schedule && Object.keys(schedule).length > 0) {
            try {
                setLoading(true)
                let records = []
                const url = 'verificacion/get-schedule-notifications?'
                const params = schedule.id.reduce((carry, id) => {
                    return carry ? carry + `&horario_id[]=${id}` : `horario_id[]=${id}`
                }, '')
                const response = await APISERVICE.get(url, params)
                if (response.success) {
                    records = JSON.parse(decryptAES(response.data))
                }
                if (records.length > 0) {
                    updateLastState(records, schedule)
                } else {
                    const infoSchedule = {
                        infoSchedule: {
                            schedule: schedule,
                            notificationSchedules: '',
                        },
                        classStarted: {
                            startClass: '',
                            active: false,
                            endClass: '',
                        },
                        activeNotification: false,
                    }
                    dispatch(updateTeacherInfo(infoSchedule))
                }
            } catch (error) {
            } finally {
                setLoading(false)
            }
        } else {
            initialState()
        }
    }

    /**
     * Verifica si existe una clase activa.
     * @returns Retorna true si existe una clase activa.
     */
    const verifyActiveClass = () => {
        let active = false
        if (classStarted.active && infoSchedule?.schedule) {
            const { dia, hora_fin } = infoSchedule.schedule
            if (dia === formatDate(serverDate.current, 'N')) {
                // Obtener clase actual.
                const endTimePlusSlack = formatDate(
                    addTime(`${dateString} ${hora_fin}`, minutesToEndClass, 'minutes'),
                    'H:i:s',
                )
                if (endTimePlusSlack && timeString < endTimePlusSlack) {
                    active = true
                }
            }
        }
        return active
    }

    /**
     * Busca la clase siguiente o en curso.
     * @param {Array<object>} schedules Horarios de las clases de día (ordenado por hora_inicio).
     * @returns Retorna la clase siguiente o en curso.
     */
    const nextClass = (schedules) => {
        let result = null
        // Obtener clase actual.
        for (const key in schedules) {
            const { hora_inicio, hora_fin } = schedules[key]
            if (hora_inicio > timeString || (hora_inicio <= timeString && timeString < hora_fin)) {
                result = schedules[key]
                break
            }
        }
        return result
    }

    const updateLastState = (dataRecords, schedule) => {
        const classStartedRecord = dataRecords.filter((value) => value.tipo_asistencia === typeChecks.START)
        const classEndRecord = dataRecords.filter((value) => value.tipo_asistencia === typeChecks.END)
        if (classEndRecord.length > 0) {
            let endClassTime = `${dateString} ${schedule.hora_fin}`
            endClassTime = subtractTime(endClassTime, 15, 'minutes')
            endClassTime = formatDate(endClassTime, 'Y-m-d H:i:s')
            if (endClassTime > dateTimeString) {
                initialState(classEndRecord[0].hora_tikeo.slice(11, 19))
            } else {
                initialState()
            }
        } else {
            const permanencia = dataRecords.reduce((carry, record) => {
                if (record.type === typeChecks.CONTROLPERMANENCIA) {
                    carry.push({
                        id: record.id,
                        hora: record.hora_tikeo.slice(11, 17),
                        tikeo: record.tikeo,
                    })
                }
                return carry
            }, [])

            const jsonString = JSON.stringify(permanencia)
            const scrypthNotification = encryptAES(jsonString, kEYLOCALSTORAGE)
            let data = {
                infoSchedule: {
                    schedule: schedule,
                    notificationSchedules: scrypthNotification,
                },
                infoTimer: {},
                classStarted: {
                    startClass: classStartedRecord[0].hora_tikeo.slice(11, 19),
                    active: true,
                },
            }
            dispatch(updateTeacherInfo(data))
        }
    }

    useEffect(() => {
        getInfo()
    }, [])
    return { duracionNotificaciones, loading, getDailySchedules }
}
